// © Microsoft Corporation. All rights reserved.

import { AutoSuggestDelayBinding, AutoSuggestProperties } from "./AutoSuggest.interfaces";
import { KeyValuePair, UrlUtility, appEnvironment, getCookie, getHeadData } from "@msnews/core";
import { canUseDOM, createScriptTag } from "@msnews/isomorphic-util";

import { isNullOrUndefined } from "util";
import { setCookie } from "@msnews/experiences-base";

/**
 * Class to download AutoSuggest and setup required handlers
 */
export class AutoSuggest {

    /// <var type="string">The auto suggest cookie value prefix. The full value looks like AS=0 when Autosuggest turns off and AS=1 when turn on.</var>
    public static readonly cookieCrumb: string = "AS";

    /// <var type="string">Denotes var name for this instance of autosuggest UI - needed for callback in JSON response.</var>
    public static readonly globalObjectName: string = "sa_inst";

    /// <var type="string">The cookie name that will be stamped if user selected "Turn off".</var>
    public static readonly cookieName: string = "SRCHHPGUSR";

    // Cookie names that will be requested from bing.com (+) delimited eg: cookie1+cookie2
    public static readonly sharedCookieName: string = "_SS";

    /// <var type="string">The Autosuggest cookie domain.</var>
    public static readonly cookieDomain: string = "msn.com";

    /// <var type="string">Session id from bing.com</var>
    private partnerSessionId: string;

    /**
     * Initializes the AutoSuggest class
     * @param {AutoSuggestDelayBinding} delayBind Delay binding property value
     * @param {string} osMarket OS Market
     * @param {string} osLanguage OS Language
     * @param {string} partnerEndpoint Partned endpoint
     * @param {string} features AutoSuggest features to be enabled
     * @param {boolean} enableNewSnrUrl Enable new snr url
     * @param {boolean} disableSuggestionFetchOnEmpty Disable suggestion fetch on empty
     * @param {boolean} useMarketBasedUrl Use market based url
     * @param {boolean} enableFeatures Enable auto suggest features
     * @param {string} Request id
     * @param {boolean} isDarkMode Flag that defines if dark mode theme is enabled
     */
    public constructor(
        private autoSuggestProperties: AutoSuggestProperties,
        private osMarket: string,
        private osLanguage: string,
        private requestId: string,
        private isDarkMode?: boolean) {
    }

    /**
     * Adds AutoSuggest script. This method will insert a script tag in head to download autosuggest.
     * @param { function } downloadCompleteAction Function to be invoked after Auto Suggest string is downloaded
     * @returns { boolean } true if Auto Suggest is successfully added, false otherwise
     */
    public addAutoSuggest(downloadCompleteAction: () => void): boolean {
        if (!canUseDOM()) {
            return false;
        }

        const isKidsMode: boolean = appEnvironment.CurrentRequestTargetScope &&
                                    appEnvironment.CurrentRequestTargetScope.audienceMode === "kids";

        if (isKidsMode) {
            return false;
        }

        const autoSuggestDisabled = this.isDisabled();
        const autoSuggestGlobalObjectName = window[AutoSuggest.globalObjectName];

        // If the script is already downloaded, directly call enable
        if (autoSuggestGlobalObjectName) {
            // AutoSuggest has a private variable called _bDisabled which needs to
            // be reset whenever we toggle autoSuggest
            autoSuggestGlobalObjectName.enable(!autoSuggestDisabled);

            // Check to see if the session id cookie was set and update it
            this.updateAutoSuggestSessionId();

            return true;
        }

        if (autoSuggestDisabled) {
            return false;
        }

        // Download autosuggest script
        const url = this.getCompleteUrl();
        const scriptElement = createScriptTag(url, undefined, true, "use-credentials");
        scriptElement.onload = downloadCompleteAction;
        const headElement = document.getElementsByTagName("head")[0];
        headElement.appendChild(scriptElement);

        return true;
    }

    /**
     * This function checks to see if the cookies from bing.com are present, if the cookies aren't there,
     * then the iFrame is created to call the url specified in sharedCookiePartnerUrl (default: http://www.bing.com/sck
     */
    public invokeCookieSharing(): void {
        if (!checkString(getCookie(AutoSuggest.sharedCookieName), 5)) {
            setCookie(AutoSuggest.sharedCookieName, "SID=00", 365, AutoSuggest.cookieDomain, "/");
        }
    }

    /**
     * A helper function to check whether Autosuggest is enabled/disabled by reading from a cookie.
     * There is an isDisabled function in autosuggest_external but this function
     * has to be defined again since we need to call this before downloading the external file.
     * @returns { boolean } True if disable, false otherwise
     */
    private isDisabled(): boolean {

        // Regular expression to get value of Autosuggest cookie.
        // Cookie value looks like AS=0 when Autosuggest turns off and AS=1 when Autosuggest turns on.
        const crumbRegex = new RegExp("\\b" + AutoSuggest.cookieCrumb + "=0\\b", "i");
        return crumbRegex.test(getCookie(AutoSuggest.cookieName));
    }

    /**
     * Updates the session id in the external file. until required as configured
     */
    private updateAutoSuggestSessionId(): void {
        const sidCookieRegEx = /SID=[\da-z]+/i;
        if (!this.partnerSessionId) {
            const cookieValue: string = getCookie(AutoSuggest.sharedCookieName);
            if (checkString(cookieValue, 4)) {
                const cookieCrumbs: RegExpMatchArray = cookieValue.match(sidCookieRegEx);
                if (cookieCrumbs && checkString(cookieCrumbs[0], 5)) {
                    this.partnerSessionId = cookieCrumbs[0].substr(4);
                }
            }
        }

        const autoSuggestGlobalObjectName = window[AutoSuggest.globalObjectName];
        if (autoSuggestGlobalObjectName) {
            const sessionIdFunction = "sid";
            const sessionIdFunctionImplementation = autoSuggestGlobalObjectName[sessionIdFunction];
            if (typeof sessionIdFunctionImplementation === "function") {
                // If the sa_autosuggest exposes a function to update the session id, call it
                sessionIdFunctionImplementation(this.partnerSessionId);
            }
        }
    }

    /**
     * Gets the final AutoSuggest url with all required query parameters
     * @return {string} AutoSuggest url
     */
    private getCompleteUrl(): string {
        let url = "";
        const currentParams = UrlUtility.getParamsFromUrl(location.search);
        if (currentParams.find(kv => kv.key === "autosuggest_hook")) {
            // if this is bing automation test request use url from query parameter(asinturl)
            return this.getHookUrl(currentParams);
        } else if (this.autoSuggestProperties.useMarketBasedUrl) {
            url = this.getMarketBasedUrl();
        } else {
            url = this.getUrl();
        }

        if (this.shouldInitializeAutoSuggestOnPageLoad()) {
            // onload=1 query delays bing autosuggest script initialization until page load. This is not needed for OnUserInteraction mode
            // as bing autosuggest script is initialized on user clicks/keypress
            url += "&onload=1";
        }

        // Append dark mode query param
        if (this.isDarkMode) {
            url += "&rt=1";
        }

        // Append zero input item count
        if (!isNullOrUndefined(this.autoSuggestProperties.zeroInputItemCount)) {
            url += `&zict=${this.autoSuggestProperties.zeroInputItemCount}`;
        }

        // Append qsp to disable search history
        if (this.autoSuggestProperties.disableSearchHistory) {
            url += "&shdis=1";
        }

        // Append qsp to disable search history
        if (!isNullOrUndefined(this.autoSuggestProperties.maxItemCount)) {
            url += `&itemCount=${this.autoSuggestProperties.maxItemCount}`;
        }

        return url;
    }

    /**
     * Should initialize autosuggest script on page load
     * @returns {boolean} True if autosuggest script should be initialized on page load, false otherwise
     */
    private shouldInitializeAutoSuggestOnPageLoad(): boolean {
        return this.autoSuggestProperties.delayBind === AutoSuggestDelayBinding.Immediately
            || this.autoSuggestProperties.delayBind === AutoSuggestDelayBinding.OnPageLoad
            || (this.autoSuggestProperties.delayBind === AutoSuggestDelayBinding.OnBusinessSearchResultsReady
                && this.autoSuggestProperties.businessSearchResultsEnabled);
    }

    /**
     * Gets market based AutoSuggest url
     * @returns {string} Market based AutoSuggest url
     */
    private getMarketBasedUrl(): string {
        /// E.g. market based url https://www.bing.com/as/init?cc=<market>&setlang=<lang>&pt=<newPartner>&refig=<refig>
        /// "cc" parameter should be set to  market property of machineState, the setlang=ab-cd value should be set using lang property of machineState.

        const market = this.osMarket || "US";
        const language = this.osLanguage || "en-US";
        const locale = getHeadData().Locale;
        const marketBasedUrl = ["https",
            "://www.bing.com/as/init?",
            "pt=msnedgentp",
            this.osLanguage ? "&setlang=" + language : "",
            this.osMarket ? "&cc=" + market : "",
            "&refig=" + this.requestId];
        let url = marketBasedUrl.join("");

        if (language.toLowerCase() !== "en-us") {
            // add the endpt=1 param for all non en-us market
            url += "&endpt=1";
        }

        url += this.getFeatures();

        // Set bing for business results parameter msbqf=true to enable, false(default) to disable
        url += this.autoSuggestProperties.businessSearchResultsEnabled ? "&msbqf=true" : "&msbqf=false";

        // Disable Trending Now suggestions in autosuggest dropdown when user clicks in search box
        if (this.autoSuggestProperties.disableSuggestionFetchOnEmpty
            && (locale && locale.toLowerCase() !== "en-us")) {
            url += "&dfoe=1";
        }

        return url;
    }

    /**
     * Gets AutoSuggest url used by bing automated test
     * @param {KeyValuePair<string,string>[]} currentParams Query string parameters as an array of key value pairs
     * @returns {string} Market based AutoSuggest url
     */
    private getHookUrl(currentParams: KeyValuePair<string, string>[]): string {
        const corpnetFdHead = "muidflt9-ismsft";
        const fdheadParam = "fdhead";
        const asinturlParam = "asinturl";
        const flightAttributes = getHeadData().CurrentFlights;
        const fdheadParamKeyValuePair = currentParams.find(kv => kv.key === fdheadParam);
        const fdheadParamValue = fdheadParamKeyValuePair ? fdheadParamKeyValuePair.value : "";
        const asIntUrlKeyValuePair = currentParams.find(kv => kv.key === asinturlParam);
        const asIntUrlParamValue = asIntUrlKeyValuePair && asIntUrlKeyValuePair.value;

        if ((flightAttributes.indexOf(corpnetFdHead) > -1 || fdheadParamValue.indexOf(corpnetFdHead) > -1) && asIntUrlParamValue) {
            const url = decodeURIComponent(asIntUrlParamValue);
            return url;
        } else {
            return this.getUrl();
        }
    }

    /**
     * Gets default AutoSuggest url
     * @returns {string} Default AutoSuggest url
     */
    private getUrl(): string {
        const locale = getHeadData().Locale || "en-us";
        const defaultUrl = ["https",
            "://www.bing.com/as/init?mkt=",
            locale,
            "&pt=",
            this.autoSuggestProperties.partnerEndpoint,
            "&refig=",
            this.requestId];
        let url = defaultUrl.join("");

        if (!this.autoSuggestProperties.enableNewSnrUrl && locale.toLowerCase() !== "en-us") {
            // add the endpt=1 param for all non en-us market
            url += "&endpt=1";
        }

        return url;
    }

    /**
     * Gets the auto suggest feature flags for the URL
     * feature query parameter to enable/disable business search result will be removed in future.
     * This parameter is only applicable within microsoft corpnet
     * TODO ketank - remove this function when bing team enables this feature 100%
     */
    private getFeatures(): string {
        if (this.autoSuggestProperties.enableFeatures && this.autoSuggestProperties.features) {
            return "&features=" + this.autoSuggestProperties.features;
        }

        return "";
    }
}

/**
 * Utility function to check if input parameter is a string and of particular minimum lenght
 * @param { any } s parameter to be tested as string type
 * @param { number } minLength length to be verified
 * @returns { boolean } True if input parameter is string and of supplied minimum length, false otherwise
 */
function checkString(s: any, minLength: number): boolean {
    return typeof s === "string" && (!minLength || s.length >= minLength);
}